import React, { useCallback, useContext, type FC } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { N400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { AiIcon as AILogo } from '@atlassian/jira-atlassian-intelligence';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger';
import { useIntl } from '@atlassian/jira-intl';
import { steps, useIssueBreakdown } from '../../controllers/context';
import { AiIssueBreakdownEntryPointContext } from '../../controllers/context-provider';
import messages from '../../messages';

type Props = {
	issueHierarchyLevel?: number | undefined;
	isCompact: boolean;
};

export const IssueBreakdownButton: FC<Props> = ({ issueHierarchyLevel, isCompact }) => {
	const [{ currentStep }, { setIssueBreakdownStep, updateStreamingStatus }] = useIssueBreakdown();
	const { entryPointActions } = useContext(AiIssueBreakdownEntryPointContext);
	const triggerRef = useEntryPointButtonTrigger(entryPointActions, true);
	const isSelected = currentStep !== steps.defaultStep;

	// Toggle to open/close issue breakdown
	const onFullViewClick = useCallback(() => {
		if (!isSelected) {
			setIssueBreakdownStep(steps.draftListStep);
			updateStreamingStatus(true);
		}
	}, [isSelected, setIssueBreakdownStep, updateStreamingStatus]);

	const commonProps = {
		ref: triggerRef,
		spacing: 'compact',
		iconBefore: <AILogo size="medium" label="" />,
		interactionName: 'auto-generated.ai-work-breakdown.ui.issue-breakdown-button.button.button',
		testId: 'ai-work-breakdown.ui.issue-breakdown-button.button',
	} as const;

	const { formatMessage } = useIntl();

	const issueNameForFormatting = {
		issueType: issueHierarchyLevel && issueHierarchyLevel > 0 ? 'issues' : 'tasks',
	};

	const buttonLabel = formatMessage(messages.buttonLabel, issueNameForFormatting);

	const buttonDefaultTooltip = formatMessage(
		messages.buttonDefaultTooltipContent,
		issueNameForFormatting,
	);

	return (
		<Tooltip content={buttonDefaultTooltip} position="right">
			<IssueBreakdownButtonStyled
				{...commonProps}
				isSelected={isSelected}
				onClick={onFullViewClick}
			>
				{!isCompact && buttonLabel}
			</IssueBreakdownButtonStyled>
		</Tooltip>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const IssueBreakdownButtonStyled = styled(Button)({
	color: token('color.text.subtlest', N400),
});
